import React, { Suspense, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Empty,
  Modal,
  Popconfirm,
  Skeleton,
  Space,
  Tag,
  Typography
} from 'antd';
import Icon, { CloseCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import Fuse from 'fuse.js';
import { useMutation } from '@tanstack/react-query';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { IntegrationItemDto } from '@api/Api';
import {
  AvailableIntegration,
  availableIntegrations
} from '@pages/AdminSettings/components/IntegrationTab/constants';
import { apiClient } from '@api/client';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';

const { Title, Paragraph, Text } = Typography;

interface IntegrationCardProps {
  integration: IntegrationItemDto & {
    typeName: string;
    description: string;
    logo?: AvailableIntegration['logo'];
    tags: AvailableIntegration['tags'];
    details?: AvailableIntegration['installedDetails'];
  };
  onDetailsClick: () => void;
  onErrorClick: () => void;
  refetchInstalledIntegrations: () => Promise<void>;
}

function IntegrationCard({
  integration,
  onDetailsClick,
  onErrorClick,
  refetchInstalledIntegrations
}: IntegrationCardProps) {
  const [name, setName] = useState(integration.name || '');
  const renameMutation = useMutation({
    mutationFn: async (name: string) => {
      return await apiClient.integration.integrationControllerRenameIntegration(
        { id: integration.id, name }
      );
    }
  });
  useEffect(() => {
    setName(integration.name || '');
  }, [integration.name]);

  const uninstallMutation = useMutation({
    mutationFn: async () => {
      return await apiClient.integration.integrationControllerUninstallIntegration(
        { id: integration.id }
      );
    },
    onSettled: async () => {
      await refetchInstalledIntegrations();
    }
  });
  const upgradeRequired =
    integration.status === 'error' &&
    integration.error?.startsWith('integration_not_allowed_');
  const card = (
    <Card
      title={
        name ? (
          <div
            title={name}
            style={{
              maxWidth: upgradeRequired ? '20ch' : '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            <Text
              onClick={(e) => e.stopPropagation()}
              editable={{
                maxLength: 255,
                text: name,
                tooltip: 'Click to edit integration name',
                triggerType: ['text', 'icon'],
                onChange: (v) => {
                  const newName = (v || name || '').trim();
                  if (newName === name) return;
                  setName(newName);
                  renameMutation.mutate(newName);
                }
              }}
            >
              {name}
            </Text>
          </div>
        ) : null
      }
      extra={
        upgradeRequired ? null : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {integration.status === 'error' ? (
              <Tag
                icon={<CloseCircleOutlined />}
                color="error"
                role="button"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onErrorClick();
                }}
              >
                Error
              </Tag>
            ) : null}
            <div onClick={(e) => e.stopPropagation()}>
              <Popconfirm
                title="Are you sure you want to uninstall this integration?"
                onConfirm={() => uninstallMutation.mutate()}
              >
                <Button
                  danger
                  type="dashed"
                  size="small"
                  loading={uninstallMutation.isLoading}
                >
                  <DeleteOutlined />
                </Button>
              </Popconfirm>
            </div>
          </div>
        )
      }
      style={{
        height: '100%',
        cursor: upgradeRequired ? 'not-allowed' : undefined
      }}
      hoverable={!!integration.details}
      onClick={upgradeRequired ? undefined : onDetailsClick}
    >
      <Card.Meta
        title={integration.typeName}
        description={
          <>
            <Paragraph>
              {integration.tags.map((x) => (
                <Tag key={x} style={{ marginTop: 8 }}>
                  {x}
                </Tag>
              ))}
            </Paragraph>
            <Paragraph>{integration.description}</Paragraph>
          </>
        }
        avatar={
          integration.logo ? <integration.logo width={64} height={64} /> : null
        }
      />
    </Card>
  );
  if (upgradeRequired) {
    const upgradeText =
      integration.error === 'integration_not_allowed_advanced'
        ? ' to an Enterprise plan to use this integration.'
        : ' to a Team plan to use this integration.';
    return (
      <Badge.Ribbon
        text={
          <UpgradePlanTooltip text={upgradeText} isCanVisible>
            <Space align="center" size={4}>
              <Icon component={UpgradePlanSvg} />
              Upgrade Required
            </Space>
          </UpgradePlanTooltip>
        }
        color="#353839"
      >
        {card}
      </Badge.Ribbon>
    );
  }
  return card;
}

interface InstalledIntegrationsProps {
  searchQuery: string;
  list: IntegrationItemDto[];
  refetchInstalledIntegrations: () => Promise<any>;
}

export default function InstalledIntegrations({
  searchQuery,
  list,
  refetchInstalledIntegrations
}: InstalledIntegrationsProps) {
  const allIntegrations = useMemo(() => {
    return list.map((x) => {
      const available = availableIntegrations[x.type];
      return {
        ...x,
        typeName: available?.name || '',
        description: available?.description || '',
        logo: available?.logo,
        tags: available?.tags || [],
        details: available?.installedDetails
      };
    });
  }, [list]);

  const fuseSearch = useMemo(
    () =>
      new Fuse(allIntegrations, {
        keys: ['name', 'typeName', 'description', 'tags'],
        threshold: 0.3
      }),
    [allIntegrations]
  );

  const integrations = useMemo(
    () =>
      searchQuery
        ? fuseSearch.search(searchQuery).map((result) => result.item)
        : allIntegrations,
    [fuseSearch, allIntegrations, searchQuery]
  );

  const [errorItem, setErrorItem] = useState<(typeof integrations)[number]>();
  const [detailsItem, setDetailsItem] = useState<
    (typeof integrations)[number] & { key: string }
  >();

  if (!integrations.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No installed integrations found"
        style={{
          gridColumnStart: 1,
          gridColumnEnd: -1
        }}
      />
    );
  }

  return (
    <>
      {integrations.map((x) => (
        <IntegrationCard
          key={x.id}
          integration={x}
          onErrorClick={() => setErrorItem(x)}
          onDetailsClick={() =>
            setDetailsItem({ ...x, key: crypto.randomUUID() })
          }
          refetchInstalledIntegrations={refetchInstalledIntegrations}
        />
      ))}
      <Modal
        open={!!detailsItem?.details}
        onCancel={() => setDetailsItem(undefined)}
        footer={null}
        width={800}
        centered
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'min(85vh, 1024px)',
            minHeight: 0
          }}
        >
          <Suspense
            fallback={
              <>
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </>
            }
          >
            {detailsItem?.details ? (
              <detailsItem.details
                key={detailsItem.key}
                uniqueKey={detailsItem.key}
                integration={detailsItem}
                onClose={() => setDetailsItem(undefined)}
              />
            ) : null}
          </Suspense>
        </div>
      </Modal>
      <Modal
        open={!!errorItem}
        footer={null}
        onCancel={() => setErrorItem(undefined)}
        width={560}
        destroyOnClose
      >
        <Title level={3} style={{ marginBottom: 8 }}>
          {errorItem?.name}
        </Title>

        <Alert
          type="error"
          showIcon
          message="The integration reported an error"
          description={
            <Paragraph style={{ marginTop: 8 }}>
              <Text code copyable>
                {errorItem?.error ??
                  'No additional error message was provided.'}
              </Text>
            </Paragraph>
          }
        />
      </Modal>
    </>
  );
}
